import styled, { css } from "styled-components";
import AntSegmented from "antd/es/segmented";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookPhoneType,
  newLookType,
} from "common/NewLook/tokens.ts";
import {
  NEW_LOOK_PHONE_CONTROL,
  NEW_LOOK_PHONE_HIT,
  phoneMaxWidth,
} from "common/NewLook/layout";

/** The track's inset around the thumb (both sides). */
const TRACK_PAD = 2;
/** Phone: thumb height that makes the track the 36px phone control. */
const PHONE_THUMB = NEW_LOOK_PHONE_CONTROL - 2 * TRACK_PAD;
/** Phone: how far a tab's hit area reaches past its thumb (40px in all). */
const PHONE_HIT_OUTSET = (NEW_LOOK_PHONE_HIT - PHONE_THUMB) / 2;

/**
 * New look: a panel of the bot form (the form, the chart, the helpers): white
 * on the canvas, a hairline, radius 16 and the soft dashboard shadow.
 */
export const formPanel = css`
  border: ${(p) => p.theme.borders.panel};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  box-shadow: ${(p) => p.theme.shadows.panel};
  background: ${(p) => getNewLookTokens(p.theme).panel};
`;

/**
 * New look: underline tabs as a panel's header (antd Tabs) — a 52px row,
 * 20px in from the panel's side, a hairline under it, the selected tab in
 * ink over the brand-blue underline (like the dashboard's panel tabs).
 */
export const panelTabs = css`
  &.ant-tabs > .ant-tabs-nav {
    margin: 0;
  }
  &.ant-tabs > .ant-tabs-nav .ant-tabs-nav-wrap {
    padding: 0 20px;
  }
  &.ant-tabs > .ant-tabs-nav::before {
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  &.ant-tabs .ant-tabs-tab {
    min-height: 52px;
    padding: 0;
  }
  &.ant-tabs .ant-tabs-tab + .ant-tabs-tab {
    margin: 0 0 0 20px;
  }
  &.ant-tabs .ant-tabs-tab .ant-tabs-tab-btn {
    ${newLookType.body};
    font-weight: 500;
    color: ${(p) => getNewLookTokens(p.theme).secondary};
    transition: color 120ms;
  }
  &.ant-tabs .ant-tabs-tab:hover .ant-tabs-tab-btn {
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
  &.ant-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${(p) => getNewLookTokens(p.theme).ink} !important;
    font-weight: 500;
  }
  &.ant-tabs .ant-tabs-ink-bar {
    height: 2px;
    border-radius: 1px;
    background: ${(p) => getNewLookTokens(p.theme).accent};
  }
`;

/** The platform's segmented control: a well track, a white thumb. */
const segmentedTrack = css`
  padding: ${TRACK_PAD}px;
  gap: ${TRACK_PAD}px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
`;
const segmentedThumb = css`
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: 0 1px 2px rgba(13, 20, 78, 0.08),
    inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
`;

/**
 * New look: antd tabs drawn as the platform's segmented control (for small
 * in-form toggles such as DCA / Custom).
 */
export const segmentedTabs = css`
  &.ant-tabs > .ant-tabs-nav {
    margin: 0 0 16px;
  }
  &.ant-tabs > .ant-tabs-nav::before,
  &.ant-tabs .ant-tabs-ink-bar {
    display: none;
  }
  &.ant-tabs > .ant-tabs-nav .ant-tabs-nav-wrap {
    padding: 0;
  }
  .ant-tabs-nav-list {
    ${segmentedTrack};
  }
  &.ant-tabs .ant-tabs-tab,
  &.ant-tabs .ant-tabs-tab + .ant-tabs-tab {
    margin: 0;
    padding: 0 12px;
    min-height: 28px;
    border-radius: ${NEW_LOOK_RADIUS.control - 2}px;
    transition: background-color 120ms, box-shadow 120ms;
  }
  &.ant-tabs .ant-tabs-tab .ant-tabs-tab-btn {
    ${newLookType.label};
    color: ${(p) => getNewLookTokens(p.theme).muted};
  }
  &.ant-tabs .ant-tabs-tab:hover .ant-tabs-tab-btn {
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
  &.ant-tabs .ant-tabs-tab.ant-tabs-tab-active {
    ${segmentedThumb};
  }
  &.ant-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${(p) => getNewLookTokens(p.theme).ink} !important;
  }
  /* Phone: a full-width 36px control (40px hit area), 14px labels, and a
     fade on the right edge when the options overflow. */
  @media (max-width: ${phoneMaxWidth}) {
    &.ant-tabs > .ant-tabs-nav .ant-tabs-nav-wrap {
      padding: ${PHONE_HIT_OUTSET - TRACK_PAD}px 0;
      margin: -${PHONE_HIT_OUTSET - TRACK_PAD}px 0;
    }
    .ant-tabs-nav-list {
      flex: 1 0 auto;
    }
    &.ant-tabs .ant-tabs-tab,
    &.ant-tabs .ant-tabs-tab + .ant-tabs-tab {
      position: relative;
      flex: 1 0 auto;
      justify-content: center;
      min-height: ${PHONE_THUMB}px;
      padding: 0 14px;
    }
    &.ant-tabs .ant-tabs-tab::before {
      content: "";
      position: absolute;
      inset: -${PHONE_HIT_OUTSET}px 0;
    }
    &.ant-tabs .ant-tabs-tab .ant-tabs-tab-btn {
      ${newLookPhoneType.tab};
    }
    &.ant-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
      font-weight: 500;
    }
    &.ant-tabs > .ant-tabs-nav .ant-tabs-nav-wrap-ping-left::before,
    &.ant-tabs > .ant-tabs-nav .ant-tabs-nav-wrap-ping-right::after {
      width: 32px;
      box-shadow: none;
      z-index: 2;
    }
    &.ant-tabs > .ant-tabs-nav .ant-tabs-nav-wrap-ping-left::before {
      background: linear-gradient(
        to left,
        transparent,
        ${(p) => getNewLookTokens(p.theme).panel}
      );
    }
    &.ant-tabs > .ant-tabs-nav .ant-tabs-nav-wrap-ping-right::after {
      background: linear-gradient(
        to right,
        transparent,
        ${(p) => getNewLookTokens(p.theme).panel}
      );
    }
  }
`;

/** The platform's segmented control as an antd Segmented (new look). */
export const FormSegmented = styled(AntSegmented)`
  &.ant-segmented {
    ${segmentedTrack};
  }
  .ant-segmented-group {
    gap: ${TRACK_PAD}px;
  }
  .ant-segmented-item,
  .ant-segmented-thumb {
    border-radius: ${NEW_LOOK_RADIUS.control - 2}px;
  }
  .ant-segmented-item {
    color: ${(p) => getNewLookTokens(p.theme).muted};
    font-weight: 500;
  }
  .ant-segmented-item:hover:not(.ant-segmented-item-selected) {
    color: ${(p) => getNewLookTokens(p.theme).ink};
    background: transparent;
  }
  .ant-segmented-item-selected,
  .ant-segmented-thumb {
    ${segmentedThumb};
    color: ${(p) => getNewLookTokens(p.theme).ink};
    font-weight: 500;
  }
  .ant-segmented-item-label {
    min-height: 26px;
    line-height: 26px;
    padding: 0 12px;
    font-size: 12.5px;
  }
` as unknown as typeof AntSegmented;

/**
 * Phone (new look): the form's Basic / Advanced switch, full width at the top
 * of the fields — a 36px control with a 40px hit area and 14px labels.
 */
export const PhoneFormSegmented = styled(FormSegmented)`
  &.ant-segmented {
    display: flex;
    width: 100%;
  }
  .ant-segmented-group {
    flex: 1;
  }
  && .ant-segmented-item {
    position: relative;
    flex: 1;
    min-width: 0;
    font-weight: 450;
  }
  .ant-segmented-item::before {
    content: "";
    position: absolute;
    inset: -${PHONE_HIT_OUTSET}px 0;
  }
  && .ant-segmented-item-selected {
    font-weight: 500;
  }
  .ant-segmented-item-label {
    min-height: ${PHONE_THUMB}px;
    line-height: ${PHONE_THUMB}px;
    padding: 0 12px;
    font-size: 14px;
    text-align: center;
  }
` as unknown as typeof AntSegmented;
